import {Expressions, Variable, Plugins} from '@packages/systems/wfdl';
import {omit} from '@packages/utilities/key-map';
import {
  type Expression,
  ERecord,
  ECall,
  ESelect,
  EVariable,
  ENumber,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {type Context} from '../types';
import {qualify, identifier} from '@packages/systems/wfdl-Qualified';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {DEFAULT_DATETIME_FIELD_SLUGS} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {DATETIME_FORMAT_OPTIONS} from '@packages/systems/dynamo/constants';
import {Collection} from '..';

type Collections = Context['extraContext']['collections'];
type NodeMap = Context['tree']['nodeMap'];
type ParentMap = Context['tree']['parentMap'];

// A map of source and target types to their corresponding WFDL
// function
const functionMap: Record<string, Record<string, Variable.VariableType>> = {
  video: {
    Video: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoVideoLinkToVideo')
      )
    ),
  },
  plainText: {
    PlainText: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoPlainTextToListOfElements')
      )
    ),
    CommercePrice: Variable.unqualified(
      identifier('dynamoPriceToListOfElements')
    ),
    Option: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoOptionToListOfElements')
      )
    ),
    Number: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoNumberToListOfElements')
      )
    ),
    Link: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoLinkToListOfElements')
      )
    ),
    Email: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoEmailToListOfElements')
      )
    ),
    Phone: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoPhoneToListOfElements')
      )
    ),
    Video: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoVideoLinkToListOfElements')
      )
    ),
    Date: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateToListOfElements')
      )
    ),
    DateTime: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateTimeToListOfElements')
      )
    ),
    //Slug type is not handled as it cannot be binded from Legacy bindings
  },
  image: {
    ImageRef: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoImageToImage'))
    ),
  },
  text: {
    PlainText: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoPlainTextToText')
      )
    ),
    CommercePrice: Variable.unqualified(identifier('dynamoPriceToText')),
    Option: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoOptionToText'))
    ),
    Number: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoNumberToText'))
    ),
    Link: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoLinkToText'))
    ),
    Email: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoEmailToText'))
    ),
    Phone: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoPhoneToText'))
    ),
    Video: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoVideoLinkToText')
      )
    ),
    Date: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateToText'))
    ),
    DateTime: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateTimeToText'))
    ),
    //Slug type is not handled as it cannot be binded from Legacy bindings
  },
  lightboxMedia: {
    Video: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoVideoLinkToListOfLightboxMedia')
      )
    ),
    ImageRef: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoImageToListOfLightboxMedia')
      )
    ),
    'Set/ImageRef': Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoImageToListOfLightboxMedia')
      )
    ),
  },
  children: {
    RichText: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoRichTextToRichTextChildren')
      )
    ),
  },
} as const;

// Util for easily extracting the migrated dyn bind field.
// Null return means the whole dyn object can be deleted
// in the patch.
export const removeLegacyDynBindField = (
  key: string,
  dynBind: {
    [key: string]: unknown;
  }
): {
  [key: string]: unknown;
} | null => {
  const bindKeys = Object.keys(dynBind);
  if (bindKeys.length === 0) {
    // we want to remove the whole data.dyn
    return null;
  } else if (bindKeys.length === 1 && bindKeys[0] === key) {
    return null;
  }

  return omit([key])(dynBind);
};

// Util for easily adding in a new expression to the
// node.bind expression. If the incoming bindRecord is
// not a record we want to return it back as is.
export const addUniversalBindingsBinding = (
  newFieldKey: string,
  newFieldValue: Expression,
  bindRecord: Expression | null
): Expression | null => {
  if (bindRecord == null) {
    return ERecord({[newFieldKey]: newFieldValue});
  }
  const boundFields = Expressions.getRecord(bindRecord);
  // We did not receive a record expression as bindRecord
  if (!boundFields) {
    return bindRecord;
  }

  const newFields = {...boundFields, [newFieldKey]: newFieldValue} as const;
  return ERecord(newFields);
};

// Util for easily creating Dynamo Gateway transformer function
// calls to the correct CMS fields
export const createNewCMSBinding = (
  legacyBindingType: string,
  targetType: string,
  property: string,
  filter?: string
): Expression | null => {
  const transformerFn = functionMap[targetType]?.[legacyBindingType];
  if (!transformerFn) {
    return null;
  }
  if (legacyBindingType === 'Date' || legacyBindingType === 'DateTime') {
    const isValidDateFilter = filter
      ? DATETIME_FORMAT_OPTIONS.includes(filter.replace('date|', ''))
      : false;
    const dateFilter =
      filter && isValidDateFilter ? filter.replace('date|', '') : '';
    return ECall({
      fun: ECall({
        fun: EVariable(transformerFn),
        arg: EEnum(dateFilter),
      }),
      arg: ESelect({
        from: EVariable(Variable.unqualified(identifier('Dynamo'))),
        prop: property,
      }),
    });
  } else if (legacyBindingType === 'Number') {
    const rawFilterValue =
      typeof filter === 'string' && filter.startsWith('numberPrecision|')
        ? filter.replace(/\D/g, '')
        : '';
    const parsedFilterValue =
      rawFilterValue === '' ? -1 : Number(rawFilterValue);
    const filterValue = Number.isNaN(parsedFilterValue)
      ? -1
      : parsedFilterValue;
    return ECall({
      fun: ECall({
        fun: EVariable(transformerFn),
        arg: ENumber(filterValue),
      }),
      arg: ESelect({
        from: EVariable(Variable.unqualified(identifier('Dynamo'))),
        prop: property,
      }),
    });
  } else {
    return ECall({
      fun: EVariable(transformerFn),
      arg: ESelect({
        from: EVariable(Variable.unqualified(identifier('Dynamo'))),
        prop: property,
      }),
    });
  }
};

const isDynamoWrapper = (node: JSONNode): boolean => {
  return node.type === 'DynamoWrapper';
};
const isDynamoItem = (node: JSONNode): boolean => {
  return node.type === 'DynamoItem';
};
const isCart = (node: JSONNode): boolean => {
  return node.type === 'CommerceCartWrapper';
};

const isFieldInCMSCollection = (
  dynBindingPath: Array<{
    slug: string;
  }>,
  collections: Collections,
  wrappingDynCollection: string
): boolean => {
  const collection = collections && collections[wrappingDynCollection];

  if (collection && getCMSCollectionId(wrappingDynCollection, collections)) {
    return collection.fields.some(
      (field) => field.slug === dynBindingPath[0]?.slug
    );
  }

  return false;
};

const getCMSCollectionId = (
  collectionId: string | null | undefined,
  collections: Collections
): string | undefined => {
  if (!collections || !collectionId) {
    return undefined;
  }
  const collection = collections[collectionId];
  return collection && (!collection.plugin || collection.plugin === 'cms')
    ? collection?._id
    : undefined;
};

const getDynamoWrapperCollectionId = (
  dynamoWrapper: JSONNode,
  collections: Collections,
  wrappingDynCollection?: string | null
): string | undefined => {
  const boundCollectionId = dynamoWrapper.data?.dyn?.collection?.id;
  if (boundCollectionId) {
    return getCMSCollectionId(boundCollectionId, collections);
  }
  const dynBindingPath = dynamoWrapper.data?.dyn?.bind?.repeater;
  if (
    dynBindingPath &&
    wrappingDynCollection &&
    isFieldInCMSCollection(dynBindingPath, collections, wrappingDynCollection)
  ) {
    return wrappingDynCollection;
  }
  return undefined;
};

const closestNodeOfType = (
  node: JSONNode,
  parentTypePredicate: (arg1: JSONNode) => boolean,
  nodeMap: NodeMap,
  parentMap: ParentMap
): JSONNode | false => {
  if (!node) {
    return false;
  }
  let parentId = parentMap.get(node._id);
  let parent = parentId && nodeMap.get(parentId);
  while (parentId && parent && !parentTypePredicate(parent)) {
    parentId = parentMap.get(parentId);
    parent = parentId && nodeMap.get(parentId);
  }
  return parent || false;
};

const closestParentCart = (
  node: JSONNode,
  nodeMap: NodeMap,
  parentMap: ParentMap
): JSONNode | false => {
  return closestNodeOfType(node, isCart, nodeMap, parentMap);
};

const closestParentDynamoWrapper = (
  node: JSONNode,
  nodeMap: NodeMap,
  parentMap: ParentMap
): JSONNode | false => {
  // In order to correctly target only nodes inside the DynamoItem and
  // return the relevant DynamoWrapper we first find the DynamoItem parent.
  // This is necessary in order to avoid incorrectly targeting other trees
  // within the DynamoWrapper such as DynamoEmpty and Pagination.
  const dynItem = isDynamoItem(node)
    ? node
    : closestNodeOfType(node, isDynamoItem, nodeMap, parentMap);
  if (!dynItem) {
    return false;
  }
  const dynWrapper = closestNodeOfType(
    dynItem,
    isDynamoWrapper,
    nodeMap,
    parentMap
  );
  return dynWrapper;
};

const getClosestCMSBoundCollectionId = (
  context: Context
): string | undefined => {
  const {collections, pageInfo} = context.extraContext;
  const pageCollection = pageInfo?.dynCollection;
  const {nodeMap, parentMap} = context.tree;
  const {jsonNode} = context.node;

  if (!collections) {
    // No collections means that we can not have a CMS bound node
    return undefined;
  }

  // We dont want to patch nodes inside Cart
  const cartParent = closestParentCart(jsonNode, nodeMap, parentMap);
  if (cartParent) {
    return undefined;
  }

  const cmsTemplatePageCollectionId = getCMSCollectionId(
    pageCollection,
    collections
  );

  const closestDynamoWrapper = closestParentDynamoWrapper(
    jsonNode,
    nodeMap,
    parentMap
  );
  if (!closestDynamoWrapper) {
    // We want to patch if node is on a template page bound to CMS
    return cmsTemplatePageCollectionId;
  }

  const furthestDynamoWrapper = closestParentDynamoWrapper(
    closestDynamoWrapper,
    nodeMap,
    parentMap
  );
  // If we have another dynamo wrapper parent our target node is in a
  // nested wrapper. We need to then use the nested wrapper as the
  // binding context.
  const bindingContext = furthestDynamoWrapper
    ? furthestDynamoWrapper.data?.dyn?.collection?.id
    : pageCollection;
  return getDynamoWrapperCollectionId(
    closestDynamoWrapper,
    collections,
    bindingContext
  );
};

// Util for determining if a node is in a binding context
// that is connected to a CMS data source.
export const isBoundToCMS = (context: Context): boolean => {
  const closestCMSBoundCollectionId = getClosestCMSBoundCollectionId(context);
  return closestCMSBoundCollectionId !== undefined;
};

export type BindingData = {
  type: string;
  slug: string;
};

export type ExtendedBindingData = {
  filter?: string;
} & BindingData;

export const getExtendedBindingData = (
  binding: unknown
): ExtendedBindingData | false => {
  const bindingData = getBindingData(binding);
  if (!bindingData) {
    return false;
  }
  // Need to redefine the type again here
  if (!Array.isArray(binding) || binding.length === 0) {
    return false;
  }
  const filter = binding[binding.length - 1].filter;
  return {
    type: bindingData.type,
    slug: bindingData.slug,
    filter,
  };
};

export const getBindingData = (binding: unknown): BindingData | false => {
  if (!Array.isArray(binding) || binding.length === 0) {
    return false;
  }
  const bindingType = binding[binding.length - 1].type;
  const bindingInnerType = binding[binding.length - 1].innerType;
  const slugArr = binding.map((part) => part.slug);
  const slug = slugArr.join('.');
  return {
    type:
      bindingType === 'Set'
        ? `${bindingType}/${bindingInnerType}`
        : bindingType,
    slug,
  };
};

type DeletableDynBindProperty = 'media' | 'innerHTML' | 'src' | 'alt';

export function deleteDynBindProperty(
  jsonNode: JSONNode,
  property: DeletableDynBindProperty
): void {
  if (jsonNode.data?.dyn?.bind?.[property]) {
    // eslint-disable-next-line webflow/no-delete
    delete jsonNode.data.dyn.bind[property];

    // if jsonNode.data.dyn.bind becomes an empty object after deleting the
    // property, we want to delete the bind property from data.dyn
    if (Object.keys(jsonNode.data.dyn.bind).length === 0) {
      // eslint-disable-next-line webflow/no-delete
      delete jsonNode.data.dyn.bind;

      // if jsonNode.data.dyn becomes an empty object after deleting bind,
      // we want to delete the dyn property from jsonNode.data
      if (Object.keys(jsonNode.data.dyn).length === 0) {
        // eslint-disable-next-line webflow/no-delete
        delete jsonNode.data.dyn;
      }
    }
  }
}

type Field = {
  id: string;
  slug: string;
  type: string;
  validations?: Record<string, any>;
};

// Date type can be either a Date or a DateTime even though both of them share the same 'Date' Type.
// To figure out which of these we are dealing with we need to check the corresponding fields validations.
// If the validations contain a format date-time then it's a datetime.
export function getDateType(
  context: Context,
  bindingData: BindingData
): string {
  if (context && context.extraContext && context.extraContext.collections) {
    const collections = context.extraContext.collections;
    if (DEFAULT_DATETIME_FIELD_SLUGS.includes(bindingData.slug)) {
      return 'DateTime';
    }
    const closestCMSBoundCollectionId = getClosestCMSBoundCollectionId(context);
    if (closestCMSBoundCollectionId === undefined) {
      return bindingData.type;
    }
    const bindingPath = bindingData.slug.split('.');
    if (bindingPath.length > 1 && bindingPath[0]) {
      return getDateTypeFromNestedPath(
        collections,
        closestCMSBoundCollectionId,
        bindingPath,
        bindingData.type
      );
    } else {
      const currentCollection = collections[closestCMSBoundCollectionId];
      if (currentCollection === undefined) {
        return bindingData.type;
      }
      const matchingField = currentCollection.fields.find(
        (field) => field.slug === bindingData.slug
      );
      if (isValidationFormatDateTime(matchingField)) {
        return 'DateTime';
      }
    }
  }
  return bindingData.type;
}

export const getDateTypeFromNestedPath = (
  collections: Collection,
  closestCMSBoundCollectionId: string,
  bindingPath: string[],
  initialType: string
) => {
  let currentColl = collections[closestCMSBoundCollectionId];
  for (const fieldSlug of bindingPath) {
    const field = currentColl?.fields.find((f) => f.slug === fieldSlug);
    if (field?.type === 'ItemRef') {
      currentColl = collections[getCollectionId(field)];
      continue;
    }
    if (
      isValidationFormatDateTime(field) ||
      (field ? DEFAULT_DATETIME_FIELD_SLUGS.includes(field.slug) : false)
    ) {
      return 'DateTime';
    }
  }
  return initialType;
};

const getCollectionId = (field?: Field) => {
  if (field && field.validations && field.validations['collectionId']) {
    return field.validations['collectionId'];
  } else {
    return undefined;
  }
};

const isValidationFormatDateTime = (field?: Field) => {
  return (
    field && field.validations && field.validations['format'] === 'date-time'
  );
};
